import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import Auth from "./Auth";
import Landing from "./Landing";
import VideoUpload from "./VideoUpload";
import StatsCard from "./StatsCard";
import CoachDashboard from "./CoachDashboard";
import FeedbackHistory from "./FeedbackHistory";
import Profile from "./Profile";
import ProfileCard from "./ProfileCard";
import Leaderboard from "./Leaderboard";
import GymRooms from "./GymRooms";
import StyleComparison from "./StyleComparison";
import ChatCoach from "./ChatCoach";

function DashboardCard({ emoji, label, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding:"35px 20px",borderRadius:"16px",textAlign:"center",cursor:"pointer",
        border: hovered ? "1px solid rgba(233,69,96,0.5)" : "1px solid rgba(255,255,255,0.06)",
        background: hovered ? "linear-gradient(135deg, rgba(233,69,96,0.15), rgba(255,107,53,0.08))" : "rgba(255,255,255,0.03)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ? "0 15px 35px rgba(233,69,96,0.15)" : "none",
        transition:"all 0.25s ease",fontFamily:"Inter, sans-serif",fontWeight:"600",color:"white"
      }}>
      <div style={{fontSize:"2.2rem",marginBottom:"12px"}}>{emoji}</div>
      <div style={{fontSize:"0.95rem",letterSpacing:"0.3px"}}>{label}</div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState("dashboard");
  const [statsKey, setStatsKey] = useState(0);
  const [profileName, setProfileName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 5000);
    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(timeout);
      if (session) {
        setUser(session.user);
        setRole(session.user.user_metadata.role);
        supabase.from("profiles").select("full_name, avatar_url").eq("id", session.user.id).single().then(({ data }) => {
          if (data?.full_name) setProfileName(data.full_name);
          if (data?.avatar_url) setAvatarUrl(data.avatar_url);
        });
      }
      setLoading(false);
    });
  }, []);

  const handleLogin = (newUser, newRole) => {
    setUser(newUser);
    setRole(newRole);
    setShowAuth(false);
    supabase.from("profiles").select("full_name, avatar_url").eq("id", newUser.id).single().then(({ data }) => {
      if (data?.full_name) setProfileName(data.full_name);
      if (data?.avatar_url) setAvatarUrl(data.avatar_url);
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setRole(null);
    setScreen("dashboard");
    setProfileName("");
    setAvatarUrl("");
    setShowAuth(false);
  };

  if (loading) return (
    <div style={{background:"#080810",minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"Inter, sans-serif"}}>
      <div style={{fontSize:"3rem",marginBottom:"20px"}}>🥊</div>
      <h1 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"3rem",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",letterSpacing:"3px",margin:0}}>FIGHTCOACH AI</h1>
      <p style={{color:"#333",marginTop:"15px",letterSpacing:"3px",fontSize:"0.8rem",textTransform:"uppercase"}}>Loading...</p>
    </div>
  );

  if (!user && !showAuth) return <Landing onGetStarted={() => setShowAuth(true)} />;
  if (!user && showAuth) return <Auth onLogin={handleLogin} onBack={() => setShowAuth(false)} />;

  const BackButton = () => (
    <button onClick={() => setScreen("dashboard")} style={{marginBottom:"25px",padding:"10px 20px",backgroundColor:"rgba(255,255,255,0.04)",color:"#666",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.9rem"}}>← Back</button>
  );

  const SectionTitle = ({ children }) => (
    <h2 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"2.5rem",letterSpacing:"2px",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:"25px",marginTop:0}}>{children}</h2>
  );

  return (
    <div style={{minHeight:"100vh",backgroundColor:"#080810",fontFamily:"Inter, sans-serif",color:"white",position:"relative",overflow:"hidden"}}>
      <style>{`
        @keyframes float1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(30px,-50px) scale(1.05)} 66%{transform:translate(-20px,20px) scale(0.95)} }
        @keyframes float2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-40px,30px) scale(1.08)} 66%{transform:translate(20px,-40px) scale(0.92)} }
      `}</style>

      <div style={{position:"fixed",inset:0,overflow:"hidden",zIndex:0,pointerEvents:"none"}}>
        <div style={{position:"absolute",top:"-20%",left:"-10%",width:"700px",height:"700px",borderRadius:"50%",background:"radial-gradient(circle, rgba(233,69,96,0.07) 0%, transparent 70%)",animation:"float1 15s ease-in-out infinite"}}/>
        <div style={{position:"absolute",bottom:"-20%",right:"-10%",width:"800px",height:"800px",borderRadius:"50%",background:"radial-gradient(circle, rgba(255,107,53,0.06) 0%, transparent 70%)",animation:"float2 18s ease-in-out infinite"}}/>
      </div>

      <div style={{position:"relative",zIndex:1}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 40px",backgroundColor:"rgba(8,8,16,0.8)",backdropFilter:"blur(20px)",borderBottom:"1px solid rgba(255,255,255,0.05)",position:"sticky",top:0,zIndex:100}}>
          <div style={{display:"flex",alignItems:"center",gap:"12px",cursor:"pointer"}} onClick={() => setScreen("dashboard")}>
            <span style={{fontSize:"1.6rem"}}>🥊</span>
            <h1 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"1.8rem",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",margin:0,letterSpacing:"2px"}}>FIGHTCOACH AI</h1>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
            <div onClick={() => setScreen("profile")} style={{display:"flex",alignItems:"center",gap:"10px",cursor:"pointer",padding:"8px 14px",borderRadius:"10px",border:"1px solid rgba(255,255,255,0.08)",backgroundColor:"rgba(255,255,255,0.03)"}}>
              <div style={{width:"30px",height:"30px",borderRadius:"50%",background: avatarUrl ? "transparent" : "linear-gradient(135deg, #e94560, #ff6b35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.9rem",overflow:"hidden",flexShrink:0}}>
                {avatarUrl ? <img src={avatarUrl} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}} /> : (role === "coach" ? "🎯" : "🥋")}
              </div>
              <span style={{color:"#888",fontSize:"0.85rem"}}>{profileName || user.email.split("@")[0]}</span>
            </div>
            <button onClick={handleLogout} style={{padding:"8px 18px",backgroundColor:"rgba(255,255,255,0.04)",color:"#666",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.85rem"}}>Log Out</button>
          </div>
        </div>

        <div style={{padding:"40px",maxWidth:"960px",margin:"0 auto"}}>
          {screen === "dashboard" && (
            <>
              <div style={{marginBottom:"35px"}}>
                <h2 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"3rem",margin:"0 0 8px 0",letterSpacing:"2px",background:"linear-gradient(135deg, #fff 0%, #666 100%)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>
                  {role === "coach" ? "COACH DASHBOARD" : "FIGHTER DASHBOARD"}
                </h2>
                <p style={{color:"#444",fontSize:"0.95rem",margin:0}}>
                  {role === "coach" ? "Manage your team and track fighter progress." : "Train hard. Get better. Climb the ranks."}
                </p>
              </div>
              {role === "fighter" && <div style={{marginBottom:"30px"}}><StatsCard key={statsKey} user={user} /></div>}
              <div style={{display:"grid",gridTemplateColumns:"repeat(2, 1fr)",gap:"16px"}}>
                {role === "fighter" && (
                  <>
                    <DashboardCard emoji="📹" label="Upload Training Video" onClick={() => setScreen("upload")} />
                    <DashboardCard emoji="📜" label="Session History" onClick={() => setScreen("history")} />
                    <DashboardCard emoji="🏆" label="Leaderboard" onClick={() => setScreen("leaderboard")} />
                    <DashboardCard emoji="🃏" label="My Fighter Card" onClick={() => setScreen("card")} />
                    <DashboardCard emoji="💬" label="Chat with Coach AI" onClick={() => setScreen("chat")} />
                    <DashboardCard emoji="🏋️" label="Gym Room" onClick={() => setScreen("gym")} />
                    <DashboardCard emoji="🌟" label="Style Comparison" onClick={() => setScreen("style")} />
                  </>
                )}
                {role === "coach" && (
                  <>
                    <DashboardCard emoji="👥" label="My Fighters" onClick={() => setScreen("fighters")} />
                    <DashboardCard emoji="📹" label="Review Videos" onClick={() => setScreen("fighters")} />
                    <DashboardCard emoji="🏆" label="Leaderboard" onClick={() => setScreen("leaderboard")} />
                    <DashboardCard emoji="📊" label="Fighter Progress" onClick={() => setScreen("fighters")} />
                    <DashboardCard emoji="🏋️" label="Gym Room" onClick={() => setScreen("gym")} />
                    <DashboardCard emoji="🌟" label="Style Comparison" onClick={() => setScreen("style")} />
                  </>
                )}
              </div>
            </>
          )}

          {screen === "upload" && <><BackButton /><VideoUpload user={user} onStatsUpdate={() => setStatsKey(k => k + 1)} /></>}
          {screen === "history" && <><BackButton /><SectionTitle>SESSION HISTORY</SectionTitle><FeedbackHistory user={user} /></>}
          {screen === "leaderboard" && <><BackButton /><Leaderboard user={user} /></>}
          {screen === "achievements" && <><BackButton /><SectionTitle>MY ACHIEVEMENTS</SectionTitle><StatsCard user={user} /></>}
          {screen === "card" && <><BackButton /><ProfileCard user={user} /></>}
          {screen === "fighters" && <><BackButton /><CoachDashboard user={user} /></>}
          {screen === "profile" && <><BackButton /><SectionTitle>MY PROFILE</SectionTitle><Profile user={user} onAvatarUpdate={setAvatarUrl} /></>}
          {screen === "chat" && <><BackButton /><ChatCoach user={user} /></>}
          {screen === "gym" && <><BackButton /><GymRooms user={user} role={role} /></>}
          {screen === "style" && <><BackButton /><StyleComparison user={user} /></>}

          {screen === "coachfeedback" && (
            <>
              <BackButton />
              <SectionTitle>COACH FEEDBACK</SectionTitle>
              <CoachFeedbackView user={user} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CoachFeedbackView({ user }) {
  const [feedbacks, setFeedbacks] = useState([]);
  useEffect(() => {
    supabase.from("coach_feedback").select("*").eq("fighter_id", user.id).order("created_at", { ascending: false }).then(({ data }) => setFeedbacks(data || []));
  }, [user]);

  if (feedbacks.length === 0) return (
    <div style={{backgroundColor:"rgba(255,255,255,0.02)",padding:"50px",borderRadius:"16px",textAlign:"center",border:"1px solid rgba(255,255,255,0.05)"}}>
      <div style={{fontSize:"3rem",marginBottom:"15px"}}>💬</div>
      <p style={{color:"#444",fontSize:"1rem"}}>No coach feedback yet. Ask your coach to add you to their team!</p>
    </div>
  );

  return (
    <div>
      {feedbacks.map(fb => (
        <div key={fb.id} style={{backgroundColor:"rgba(255,255,255,0.02)",padding:"25px",borderRadius:"16px",marginBottom:"15px",border:"1px solid rgba(255,255,255,0.06)"}}>
          <p style={{color:"#ccc",margin:"0 0 12px 0",lineHeight:"1.7",fontSize:"0.95rem"}}>{fb.message}</p>
          <p style={{color:"#333",margin:0,fontSize:"0.82rem"}}>{new Date(fb.created_at).toLocaleDateString()}</p>
        </div>
      ))}
    </div>
  );
}

export default App;
